// Account flows: signup wizard, upgrade form, customer settings

// ---- Shared helpers ----

function initValidation(form: JQuery): void {
    if ((form as any).validationEngine) {
        (form as any).validationEngine({ promptPosition: 'topRight', scroll: false });
    }
}

function clientValid(form: JQuery): boolean {
    if (!(form as any).validationEngine) return true;
    return (form as any).validationEngine('validate');
}

async function postAjax<T = any>(url: string, data: Record<string, any>): Promise<T> {
    return new Promise((resolve, reject) => {
        $.ajax({
            url,
            type: 'POST',
            data,
            dataType: 'json',
            success: resolve,
            error: (xhr) => {
                try { reject(JSON.parse(xhr.responseText)); }
                catch { reject({ message: 'Network error. Please try again.' }); }
            },
        });
    });
}

function formData(form: JQuery): Record<string, any> {
    const out: Record<string, any> = {};
    form.find('input, select, textarea').each(function () {
        const el = this as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
        if (!el.name || el.disabled) return;
        const type = (el as HTMLInputElement).type?.toLowerCase();
        if (type === 'checkbox' || type === 'radio') {
            if ((el as HTMLInputElement).checked) out[el.name] = el.value;
        } else {
            out[el.name] = el.value;
        }
    });
    return out;
}

function showBanner(selector: string, msg: string, isError = true): void {
    const el = $(selector);
    el.removeClass('red-gradient green-gradient')
      .addClass(isError ? 'red-gradient' : 'green-gradient')
      .html(msg)
      .show();
    $('html, body').animate({ scrollTop: el.offset()?.top ?? 0 }, 250);
}

function clearBanner(selector: string): void {
    $(selector).hide().text('');
}

function showFieldErrors(errors: Record<string, string>): void {
    clearFieldErrors();
    Object.entries(errors).forEach(([field, msg]) => {
        const input = $(`[name="${field}"]`);
        const li = input.closest('li');
        li.addClass('has-error');
        let errEl = li.find('.field-error');
        if (!errEl.length) errEl = $('<span class="field-error"></span>').appendTo(li);
        errEl.text(msg).show();
    });
    const first = $('.has-error').first();
    if (first.length) $('html, body').animate({ scrollTop: (first.offset()?.top ?? 0) - 80 }, 250);
}

function clearFieldErrors(): void {
    $('.field-error').text('').hide();
    $('.has-error').removeClass('has-error');
}

function setLoading(btn: JQuery, loading: boolean): void {
    if (loading) {
        if (!btn.data('orig-text')) btn.data('orig-text', btn.text());
        btn.prop('disabled', true).text('Please wait…');
    } else {
        btn.prop('disabled', false).text(btn.data('orig-text') || btn.text());
    }
}

function showWizardStep(stepId: string | number, tabSelector: string, stepSelector: string): void {
    $(stepSelector).hide();
    $(`#step-${stepId}`).show();
    $(tabSelector).removeClass('active');
    $(`${tabSelector}[data-step="${stepId}"]`).addClass('active');
}

// ---- Signup wizard ----

export function initSignup(planId: number, planIsFree: boolean): void {
    const banner = '#signup-banner';

    $(document).ready(() => {
        setDropdowns();
        showWizardStep(1, '.wizard-tab', '.wizard-step');
        initValidation($('#step1-form'));
        initValidation($('#step2-form'));

        // Step 1 → validate → step 2 (or direct submit for free plan)
        $('#step1-next, #step1-submit').on('click', async function () {
            const btn = $(this);
            clearBanner(banner);
            if (!clientValid($('#step1-form'))) return;
            setLoading(btn, true);

            try {
                const data = formData($('#step1-form'));
                const res = await postAjax(`/signup/${planId}/validate`, data);

                if (!res.success) {
                    if (res.errors) {
                        showFieldErrors(res.errors);
                        if (res.errors.captcha_code) refreshCaptcha();
                    }
                    showBanner(banner, res.message || 'Please correct the errors below.');
                    return;
                }

                if (planIsFree) {
                    // Free plan: submit directly from step 1 data
                    const submitRes = await postAjax(`/signup/${planId}/submit`, {});
                    if (submitRes.success) {
                        window.location.href = submitRes.redirect || '/dashboard';
                    } else {
                        showBanner(banner, submitRes.message || 'Signup failed. Please try again.');
                    }
                } else {
                    showWizardStep(2, '.wizard-tab', '.wizard-step');
                }
            } catch (e: any) {
                showBanner(banner, e.message || 'An error occurred. Please try again.');
            } finally {
                setLoading(btn, false);
            }
        });

        // Step 2 back button
        $('#step2-back').on('click', () => {
            clearBanner(banner);
            clearFieldErrors();
            showWizardStep(1, '.wizard-tab', '.wizard-step');
        });

        // Step 2 → submit
        $('#step2-submit').on('click', async function () {
            const btn = $(this);
            clearBanner(banner);
            if (!clientValid($('#step2-form'))) return;
            setLoading(btn, true);

            try {
                const data = formData($('#step2-form'));
                const res = await postAjax(`/signup/${planId}/submit`, data);

                if (!res.success) {
                    if (res.errors) showFieldErrors(res.errors);
                    showBanner(banner, res.message || 'Payment failed. Please try again.');
                    return;
                }
                window.location.href = res.redirect || '/dashboard';
            } catch (e: any) {
                showBanner(banner, e.message || 'An error occurred. Please try again.');
            } finally {
                setLoading(btn, false);
            }
        });

        // Coupon
        $('#cpnBtn').on('click', async function () {
            const coupon = String($('#coupon').val() ?? '');
            try {
                const res = await postAjax(`/signup/${planId}/coupon`, { coupon });
                if (res.success) {
                    $('#cartTotals').html(res.cart_html);
                } else {
                    showBanner(banner, res.message || 'Coupon is not valid.', true);
                }
            } catch {
                showBanner(banner, 'Could not apply coupon. Please try again.');
            }
        });
    });
}

// ---- Upgrade form ----

export function initUpgrade(planId: number): void {
    const banner = '#upgrade-banner';

    $(document).ready(() => {
        setDropdowns();
        initValidation($('#upgrade-form'));

        $('#upgrade-submit').on('click', async function () {
            const btn = $(this);
            clearBanner(banner);
            if (!clientValid($('#upgrade-form'))) return;
            setLoading(btn, true);

            try {
                const data = formData($('#upgrade-form'));
                const res = await postAjax(`/upgrade/${planId}/submit`, data);

                if (!res.success) {
                    if (res.errors) showFieldErrors(res.errors);
                    showBanner(banner, res.message || 'Upgrade failed. Please try again.');
                    return;
                }
                window.location.href = res.redirect || '/dashboard';
            } catch (e: any) {
                showBanner(banner, e.message || 'An error occurred. Please try again.');
            } finally {
                setLoading(btn, false);
            }
        });

        $('#cpnBtn').on('click', async function () {
            const coupon = String($('#coupon').val() ?? '');
            try {
                const res = await postAjax(`/upgrade/${planId}/coupon`, { coupon });
                if (res.success) {
                    $('#cartTotals').html(res.cart_html);
                } else {
                    showBanner(banner, res.message || 'Coupon is not valid.', true);
                }
            } catch {
                showBanner(banner, 'Could not apply coupon. Please try again.');
            }
        });
    });
}

// ---- Customer settings ----

export function initCustomer(): void {
    const banner = '#customer-banner';

    $(document).ready(() => {
        setDropdowns();
        initValidation($('#profile-form'));
        initValidation($('#cc-form'));

        // Tab switching
        $('.tab-btn').on('click', function () {
            const tab = $(this).data('tab') as string;
            $('.customer-tab').hide();
            $(`#tab-${tab}`).show();
            $('.tab-btn').removeClass('active');
            $(this).addClass('active');
            clearBanner(banner);
            clearFieldErrors();
        });

        // Profile save
        $('#save-profile').on('click', async function () {
            const btn = $(this);
            clearBanner(banner);
            if (!clientValid($('#profile-form'))) return;
            setLoading(btn, true);

            try {
                const data = formData($('#profile-form'));
                const res = await postAjax('/customer/save', data);

                if (!res.success) {
                    if (res.errors) showFieldErrors(res.errors);
                    showBanner(banner, res.message || 'Update failed.');
                    return;
                }
                showBanner(banner, res.message || 'Settings updated.', false);
            } catch (e: any) {
                showBanner(banner, e.message || 'An error occurred. Please try again.');
            } finally {
                setLoading(btn, false);
            }
        });

        // CC save
        $('#save-cc').on('click', async function () {
            const btn = $(this);
            clearBanner(banner);
            if (!clientValid($('#cc-form'))) return;
            setLoading(btn, true);

            try {
                const data = formData($('#cc-form'));
                const res = await postAjax('/customer/save', data);

                if (!res.success) {
                    if (res.errors) showFieldErrors(res.errors);
                    showBanner(banner, res.message || 'Card update failed.');
                    return;
                }
                showBanner(banner, res.message || 'Card updated.', false);
            } catch (e: any) {
                showBanner(banner, e.message || 'An error occurred. Please try again.');
            } finally {
                setLoading(btn, false);
            }
        });

        // Text settings save
        $('#save-texts').on('click', async function () {
            const btn = $(this);
            clearBanner(banner);
            setLoading(btn, true);

            try {
                const data = formData($('#texts-form'));
                const res = await postAjax('/customer/save', data);

                if (!res.success) {
                    showBanner(banner, res.message || 'Update failed.');
                    return;
                }
                showBanner(banner, res.message || 'Settings updated.', false);
            } catch (e: any) {
                showBanner(banner, e.message || 'An error occurred. Please try again.');
            } finally {
                setLoading(btn, false);
            }
        });

        // Cancel subscription
        $(document).on('click', '.cancel-sub', async function () {
            if (!confirm('Are you sure you want to cancel this subscription?')) return;
            const pid = $(this).data('pid');

            try {
                const res = await postAjax('/customer/cancel', { pid });
                if (res.success) {
                    showBanner(banner, res.message || 'Subscription cancelled.', false);
                    setTimeout(() => location.reload(), 1200);
                } else {
                    showBanner(banner, res.message || 'Cancellation failed.');
                }
            } catch (e: any) {
                showBanner(banner, e.message || 'An error occurred.');
            }
        });
    });
}

// ---- License modal ----

export function initLicense(): void {
    if ($('#pinfoedit').length === 0) return;

    let modal: any;

    function cookieVal(): string | null {
        const match = document.cookie.split(';').find(c => c.trim().startsWith('license='));
        return match ? decodeURIComponent(match.trim().split('=')[1]) : null;
    }

    function setCookie(val: string): void {
        const exp = new Date();
        exp.setDate(exp.getDate() + 1);
        document.cookie = `license=${encodeURIComponent(val)}; expires=${exp.toUTCString()}; path=/`;
    }

    function acceptLicense(): void {
        if (modal) modal.closeModal();
        $('#pinfoedit').show();
        $('#msg_box').hide();
    }

    function checkButton(): void {
        if (($('#cbLicense')[0] as HTMLInputElement)?.checked) {
            setCookie('1');
            acceptLicense();
        } else {
            setCookie('0');
        }
    }

    function openModal(): void {
        if (($ as any).modal == null) { $('#pinfoedit').show(); return; }

        modal = ($ as any).modal({
            contentAlign: 'center',
            width: 400,
            title: 'Accept License Agreement',
            content: `LICENSE AGREEMENT — PLEASE READ<br><br>
You have opted into a monthly subscription with MBSPros.com. We will bill you each month
for your subscription fee. You can cancel at any time. If you cancel after a billing date,
there is no pro-rata refund, but you will not be billed again. This subscription is for
one user only — sharing login credentials is cause for termination.<br><br>
<strong>WE DO NOT PROVIDE RATE LOCK RECOMMENDATIONS OR FINANCIAL ADVICE. THIS SYSTEM
IS NOT FOR MANAGING OR TRADING MORTGAGE BACKED SECURITIES.</strong><br><br>
Site content may not be redistributed in any form.<br><br>
<label><input type="checkbox" id="cbLicense" name="cbLicense"> I agree to the Terms and Conditions</label>`,
            buttons: {},
            scrolling: false,
            actions: {},
            onOpen() { modal = this; },
        });
    }

    $(document).ready(() => {
        if (cookieVal() === '1') { acceptLicense(); } else { openModal(); }
    });
    $(document).on('change', '#cbLicense', checkButton);

    // Expose for any legacy onclick attributes in modal HTML
    (window as any).checkButton = checkButton;
    (window as any).checkLicense = (): boolean => {
        if (cookieVal() !== '1') {
            alert('Please accept the license agreement before continuing.');
            return false;
        }
        return true;
    };
}

// ---- Dev test-fill (~ key) ----

export function initDevFill(): void {
    const profile: Record<string, string> = {
        fname:               'Craig Collins',
        // unique suffix avoids "email already exists" on repeated test runs
        email:               `test${Date.now()}@example.com`,
        password:            'password123',
        password2:           'password123',
        phone:               '5555551234',
        cell_phone_carrier:  'att',
        address_1:           '123 Main Street',
        address_2:           '',
        city:                'Springfield',
        state:               'IL',
        postal_code:         '62701',
        comp_name:           'Test Mortgage Co',
        comp_address:        '123 Main Street',
        comp_phone:          '5555551234',
    };

    const cc: Record<string, string> = {
        cc_name:    'Craig Collins',
        cc_no:      '4111111111111111',
        cc_type:    'visa',
        cc_exp_mo:  '12',
        cc_exp_yr:  '2030',
    };

    $(document).on('keydown', function (e) {
        if (e.key !== '~') return;
        e.preventDefault();

        // Regenerate email each press so repeated fills don't hit duplicate-email check
        profile.email = `test${Date.now()}@example.com`;

        const fill = (data: Record<string, string>) => {
            Object.entries(data).forEach(([name, val]) => {
                const el = $(`[name="${name}"]`);
                if (!el.length) return;
                el.val(val).trigger('change');
            });
        };

        fill(profile);
        fill(cc);
    });
}

// ---- Internal helpers ----

function refreshCaptcha(): void {
    const img = $('#captcha') as JQuery<HTMLImageElement>;
    if (img.length) {
        img.attr('src', '/securimage/securimage_show?' + Date.now());
        $('[name="captcha_code"]').val('');
    }
}

function setDropdowns(): void {
    const carrier = (window as any)._accts_carrier;
    const ccType  = (window as any)._accts_cc_type;
    const ccMo    = (window as any)._accts_cc_exp_mo;
    const ccYr    = (window as any)._accts_cc_exp_yr;

    if (carrier) $('#cell_phone_carrier').val(carrier);
    if (ccType)  $('#cc_type').val(ccType);
    if (ccMo)    $('#cc_exp_mo').val(ccMo);
    if (ccYr)    $('#cc_exp_yr').val(ccYr);
}
